"use client";
import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Form,
  Input,
  Select,
  Button,
  Card,
  Typography,
  Space,
  Modal,
  message,
  Upload,
  Switch,
  Pagination,
  Table,
  Tooltip,
} from "antd";
import {
  UserAddOutlined,
  PlusOutlined,
  KeyOutlined,
  EditOutlined,
  DownloadOutlined,
  SearchOutlined,
  UploadOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import API_MANAGER from "../../API";

const { Title } = Typography;
const { Option } = Select;

const generateRandomKey = (length = 14) => {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  return Array.from({ length }, () =>
    chars.charAt(Math.floor(Math.random() * chars.length))
  ).join("");
};
const formatBucket = (str) => {
  if (str === "payroll") {
    return "Payroll/HRIS";
  } else if (str === "workWear") {
    return "Workwear";
  }else if(str === "ats"){
    return "ATS"
  } 
  else {
    return str
      .replace(/([A-Z])/g, " $1")    
      .replace(/^./, (c) => c.toUpperCase()) 
      .trim();
  }
};


const ExternalClient = () => {
  const [form] = Form.useForm();
  const [clients, setClients] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [logoBase64, setLogoBase64] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [clientToDelete, setClientToDelete] = useState(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);

  const pageSize = 7;
  const buckets = [
    "workWear",
    "medicals",
    "jobBoard",
    "ats",
    "compliance",
    "payroll",
  ];

  const showModal = (client = null) => {
    setModalVisible(true);

    if (!client) {
      form.resetFields();
      setLogoBase64(null);
      setEditingClient(null);
    } else {
      setEditingClient(client);
      setLogoBase64(client.logo || null);
    }
  };

  useEffect(() => {
    if (modalVisible && editingClient) {
      form.setFieldsValue({
        name: editingClient.name || "",
        email: editingClient.email || "",
        contactNo: editingClient.contactNo || "",
        bucket: editingClient.bucket || editingClient.basket || "",
        key: editingClient.key || "",
        isActive: editingClient.isActive ?? true,
      });
    } else if (modalVisible && !editingClient) {
      form.setFieldsValue({ isActive: true });
    }
  }, [modalVisible, editingClient]);

  const hideModal = () => {
    setModalVisible(false);
    setEditingClient(null);
    form.resetFields();
    setLogoBase64(null);
  };

  const onFinish = async (values) => {
    try {
      const originalKey = values.key;

      const payload = {
        ...values,
        basket: values.bucket,
        logo: logoBase64,
        isActive: values.isActive ?? true,
      };
      delete payload.bucket;

      if (editingClient) {
        const newKey = values.key;
        const existingKey = editingClient.key;

        if (newKey && newKey !== existingKey) {
          try {
            await API_MANAGER.updateExternalClientKey({
              id: editingClient._id,
              newKey: newKey,
            });

            const updatedClient = {
              ...editingClient,
              key: newKey,
            };

            setClients((prev) =>
              prev.map((client) =>
                client._id === editingClient._id ? updatedClient : client
              )
            );

            message.success("Key updated successfully!");
            downloadCSV(updatedClient);
          } catch (err) {
            console.error("Failed to update key", err);
            message.error("Failed to update key");
            return;
          }
        } else {
          message.info("No changes in key to update");
        }

        hideModal();
      } else {
        const res = await API_MANAGER.createExternalClient(payload);
        const newClient = res.data.data;
        const clientWithOriginalKey = {
          ...newClient,
          key: originalKey,
          logo: logoBase64,
        };
        setClients((prev) => [clientWithOriginalKey, ...prev]);
        message.success("Client added successfully!");
        downloadCSV(clientWithOriginalKey);

        setTimeout(async () => {
          try {
            await API_MANAGER.hashExternalClientKey(newClient._id, originalKey);
          } catch (hashErr) {
            console.error("Key hashing failed:", hashErr);
          }
        }, 5000);

        hideModal();
      }
    } catch (error) {
      console.error(error);
      message.error("Failed to save client. Please try again.");
    }
  };

  const downloadCSV = (client) => {
    const csvContent = `Name,Email,Key,Bucket\n"${client.name}","${
      client.email
    }","${client.key}","${client.bucket || client.basket}"`;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${client.name.replace(/\s/g, "_")}_info.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleGenerateKey = () => {
    const key = generateRandomKey();
    form.setFieldsValue({ key });
    navigator.clipboard
      .writeText(key)
      .then(() => message.success("Key generated and copied!"));
  };

  const beforeUpload = (file) => {
    if (!file.type.startsWith("image/")) {
      message.error("Only image files are allowed.");
      return Upload.LIST_IGNORE;
    }
    const reader = new FileReader();
    reader.onload = () => setLogoBase64(reader.result);
    reader.readAsDataURL(file);
    return false;
  };

  const handleToggleStatus = async (clientId, currentDisabledStatus) => {
    const newDisabled = !currentDisabledStatus;

    try {
      await API_MANAGER.updateExternalClientStatus({
        id: clientId,
        data: {
          disable: newDisabled,
        },
      });

      setClients((prev) =>
        prev.map((client) =>
          client._id === clientId
            ? { ...client, disabled: newDisabled }
            : client
        )
      );

      message.success(
        `Client ${newDisabled ? "disabled" : "enabled"} successfully`
      );
    } catch (err) {
      message.error("Failed to update status");
      console.error(err);
    }
  };

  const confirmDeleteClient = (client) => {
    setClientToDelete(client);
    setDeleteModalVisible(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await API_MANAGER.deleteExternalClient({ id: clientToDelete._id });
      setClients((prev) => prev.filter((c) => c._id !== clientToDelete._id));
      message.success("Client deleted successfully");
    } catch (error) {
      message.error("Failed to delete client");
    } finally {
      setDeleteModalVisible(false);
      setClientToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalVisible(false);
    setClientToDelete(null);
  };

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await API_MANAGER.getAllExternalClient();
        setClients(res?.data?.data || []);
      } catch {
        message.error("Failed to fetch clients");
      }
    };
    fetchClients();
  }, []);

  const filteredClients = clients.filter((client) =>
    client.name?.toLowerCase().includes(searchText.toLowerCase())
  );

  const paginatedClients = filteredClients.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );
      const capitalizeWords = (str) =>
  str.replace(/\b\w/g, (char) => char.toUpperCase());

  const columns = [
    {
      title: "No.",
      render: (_, __, index) => (
        <span>
          <div
            style={{
              width: 6,
              height: 24,
              // backgroundColor: __.isActive ? "green" : "orange",
              display: "inline-block",
              borderRadius: 2,
              marginRight: 6,
            }}
          />
          {index + 1 + (currentPage - 1) * pageSize}
        </span>
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
       render: (text) => capitalizeWords(text),
    },
    {
      title: "Email",
      dataIndex: "email",
    },
    {
      title: "Logo",
      dataIndex: "logo",
      render: (logo) =>
        logo ? (
          <img
            src={logo}
            alt="Client Logo"
            style={{ width: 60, height: 40, objectFit: "contain" }}
          />
        ) : (
          <span style={{ color: "#999" }}>No Logo</span>
        ),
    },
    {
      title: "Basket",
      dataIndex: "basket",
      render: (text) => formatBucket(text || ""),
    },

    {
      title: "Enable / Disable",
      render: (_, record) => (
        <Switch
          checked={!record.disabled}
          onChange={() => handleToggleStatus(record._id, record.disabled)}
        />
      ),
    },

    {
      title: "Action",
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => showModal(record)}
            />
          </Tooltip>
          <Tooltip title="Download CSV">
            <Button
              type="link"
              icon={<DownloadOutlined />}
              onClick={() => downloadCSV(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="link"
              icon={<DeleteOutlined />}
              danger
              onClick={() => confirmDeleteClient(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  

  return (
    <Card
      style={{ marginTop: "20px" }}
      title={
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={3}>3rd party platforms</Title>
          </Col>
          <Col>
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search by name"
              value={searchText}
              allowClear={true}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 250, marginRight: 10, height: 35 }}
            />
            <Button
              className="bulk_button"
              icon={<PlusOutlined />}
              onClick={() => showModal()}
            >
              Add Client
            </Button>
          </Col>
        </Row>
      }
    >
      <Table
        columns={columns}
        dataSource={paginatedClients}
        rowKey={(record) => record._id}
        pagination={false}
      />

      <Pagination
        current={currentPage}
        onChange={setCurrentPage}
        total={filteredClients.length}
        pageSize={pageSize}
        style={{ textAlign: "right", marginTop: 16 }}
      />
  

      <Modal
        title={editingClient ? "Edit 3rd party platform" : "Add 3rd party platform"}
        open={modalVisible}
        onCancel={hideModal}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            label="Client Name"
            name="name"
            rules={[{ required: true }]}
          >
            <Input disabled={!!editingClient} />
          </Form.Item>

          <Form.Item
            label="Email"
            name="email"
            rules={[{ required: true, type: "email" }]}
          >
            <Input disabled={!!editingClient} />
          </Form.Item>

          <Form.Item label="Bucket" name="bucket" rules={[{ required: true }]}>
            <Select disabled={!!editingClient}>
              {buckets.map((b) => (
                <Option key={b}>{formatBucket(b)}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Upload Logo" name="logo">
            {!logoBase64 ? (
              <Upload
                accept="image/*"
                showUploadList={false}
                beforeUpload={beforeUpload}
                disabled={!!editingClient}
              >
                <Button icon={<UploadOutlined />} disabled={!!editingClient}>
                  Click to Upload Logo
                </Button>
              </Upload>
            ) : (
              <div style={{ position: "relative" }}>
                <img src={logoBase64} alt="Preview" style={{ width: "40%" }} />
                {!editingClient && (
                  <Button
                    danger
                    onClick={() => setLogoBase64(null)}
                    style={{ position: "absolute", top: 5, right: 5 }}
                  >
                    Remove
                  </Button>
                )}
              </div>
            )}
          </Form.Item>

          <Form.Item label="Access Key" name="key" rules={[{ required: true }]}>
            <Input
              addonAfter={
                <Button
                  icon={<KeyOutlined />}
                  onClick={handleGenerateKey}
                  // disabled={!editingClient}
                />
              }
              // disabled={!editingClient}
            />
          </Form.Item>

          {/* <Form.Item label="Active" name="isActive" valuePropName="checked">
            <Switch disabled={!!editingClient} />
          </Form.Item> */}

          <Row className="buttonCol" gutter={8}>
            <Col>
              <Button onClick={hideModal} className="cancelBtn">
                Cancel
              </Button>
            </Col>
            <Col>
              <Button
                className="btn"
                type="primary"
                htmlType="submit"
                icon={<UserAddOutlined />}
              >
                {editingClient ? "Update" : "Save"}
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
      <Modal
        className="candidateDeleteModal"
        centered
        open={deleteModalVisible}
        onCancel={handleCancelDelete}
        footer={null}
        title={`Delete ${clientToDelete?.name}?`}
      >
        <Row className="header">
          <Col>
            <p className="desc">
              Are you sure you want to delete the client{" "}
              <strong>{clientToDelete?.name}</strong>?
            </p>
          </Col>
        </Row>

        <Row align="middle" gutter={16} justify="end">
          <Col>
            <Button
              className="confirmBtn"
              onClick={handleConfirmDelete}
              
              loading={false}
            >
              Delete
            </Button>
          </Col>
          <Col>
            <Button className="cancelBtn" onClick={handleCancelDelete}>
              Cancel
            </Button>
          </Col>
        </Row>
      </Modal>
    </Card>
  );
};

export default ExternalClient;
